import withParams from "@src/components/widgets/HOC/withParams";
import React, { useMemo, useEffect } from "react";
import { WidgetTemplateComponentType } from "@src/interface/Widget";
import { ILandingStandardWidgetHighlightProductAdrienCachotParams } from "../interface";
import useCtaAction from "@src/components/widgets/hooks/useCtaAction";
import type { ICompositionVariant } from "design-system/src/Components/WidgetPart/LandingHighlighProduct/LandingHighlighProduct";
import useCompositionBox from "@src/components/widgets/hooks/useCompositionBox";
import htmlParse from "html-react-parser";
import { useTranslation } from "@src/hooks/useTranslation";
import LandingHighlighProductAdrienCachot from "design-system/src/Components/WidgetPart/LandingHighlighProduct/LandingHighlighProductAdrienCachot/LandingHighlighProductAdrienCachot";
import useBreakpoint from "design-system/src/Components/Grid/hooks/useBreakpoint";
import { lazyGsap } from "design-system/src/lib/gsap/lazyGsap";

const MOBILE_STICKY_TOP = 64; // px — offset du sticky depuis le top du viewport

const HighlightProductAdrienCachot: WidgetTemplateComponentType =
  withParams<ILandingStandardWidgetHighlightProductAdrienCachotParams>(
    (props) => {
      const { p } = props;
      const { mobile } = useBreakpoint();
      const cta_action = p("cta_action");
      const isAddToCart: boolean = cta_action === "add_to_cart";
      const id_product = p("product") || p("id_product");
      const product_image = p("product_image");
      const ctaLabel = p("cta_label") || "";
      const hideCta = p("hide_cta") === "1";
      const { t } = useTranslation("Product");

      const { generateClick, getSubmitLabel } = useCtaAction(id_product || "");
      const { prepareCompositions, prepareProduct } = useCompositionBox();

      const productModel = prepareProduct(p("id_product") || "");

      const handleClick = generateClick(
        cta_action && cta_action === "open_link"
          ? { type: cta_action, link: p("link") || "#" }
          : { type: cta_action || "product_card" }
      );

      const compositions: ICompositionVariant | undefined = useMemo(() => {
        const source = p("composition_source") || "box";
        if (source === "box") {
          const productIds: string[] = (p("box_products") || "")
            .split(",")
            .map((id) => id.trim());
          const productQuantity: number[] = (p("box_products_itemnbr") || "")
            .split(",")
            .map((i) => +i);
          const newProductIds: string[] = (p("box_products_new") || "")
            .split(",")
            .map((id) => id.trim());
          return {
            type: "box",
            list:
              prepareCompositions(productIds, productQuantity, newProductIds) ||
              [],
          } as ICompositionVariant;
        } else if (source === "paragraph") {
          return {
            type: "paragraph",
            text: htmlParse(p("composition_paragraph") || ""),
          } as ICompositionVariant;
        }
        return undefined;
      }, [p, prepareCompositions]);

      const displayFromMention =
        p("product_display_from_mention") === "1"
          ? t("À partir de", "Product")
          : undefined;

      const vaePrice = productModel?.getPriceVae();
      const vadPrice = productModel?.getPriceVad();

      useEffect(() => {
        if (typeof window === "undefined" || !mobile) return;

        let cancelled = false;
        const ctxList: Array<{ revert: () => void }> = [];
        const gsapRef: { current: typeof import("gsap").gsap | null } = {
          current: null,
        };

        void lazyGsap().then(({ gsap }) => {
          if (cancelled) return;
          gsapRef.current = gsap;

          const instances = document.querySelectorAll<HTMLElement>(
            '.widget-instance[data-template="highlight-product-adrien-cachot"]'
          );

          instances.forEach((widgetInstance) => {
            if (widgetInstance.dataset.prevAnimated) return;
            widgetInstance.dataset.prevAnimated = "1";

            const prevWidget =
              widgetInstance.previousElementSibling as HTMLElement | null;
            if (!prevWidget) return;

            gsap.set(prevWidget, {
              position: "sticky",
              top: MOBILE_STICKY_TOP,
              transformOrigin: "top center",
              willChange: "transform, opacity",
              z: 0,
              zIndex: 0,
            });

            const ctx = gsap.context(() => {
              gsap.to(prevWidget, {
                scale: 0.85,
                opacity: 0,
                ease: "none",
                force3D: true,
                scrollTrigger: {
                  trigger: widgetInstance,
                  start: "top bottom",
                  end: "top top",
                  scrub: true,
                  invalidateOnRefresh: true,
                },
              });
            });

            ctxList.push(ctx);
          });
        });

        return () => {
          cancelled = true;
          ctxList.forEach((ctx) => ctx.revert());
          document
            .querySelectorAll<HTMLElement>(
              '.widget-instance[data-template="highlight-product-adrien-cachot"]'
            )
            .forEach((el) => {
              delete el.dataset.prevAnimated;
              const prev = el.previousElementSibling as HTMLElement | null;
              if (prev && gsapRef.current) {
                gsapRef.current.set(prev, {
                  position: "",
                  top: "",
                  willChange: "auto",
                  zIndex: "",
                });
              }
            });
        };
      }, [mobile]);

      return (
        <LandingHighlighProductAdrienCachot
          imageId={product_image}
          title={p("title") || ""}
          highlitedTitle={p("subtitle")}
          ctaLabel={isAddToCart ? getSubmitLabel(false, ctaLabel) : ctaLabel}
          description={p("description")}
          onClick={handleClick}
          compositionLabel={p("composition") || ""}
          compositions={compositions}
          price={productModel?.formatPrice()}
          priceSubtitle={
            productModel && productModel.getQuantity() > 1
              ? productModel.displayPieces()
              : undefined
          }
          product_display_from_mention={displayFromMention}
          // TODO: to confirm if we need to display the VAE/VAD prices
          // product_price_vae={vaePrice}
          // product_price_vad={vadPrice}
          vaeLabel={t("À emporter", "Product")}
          vadLabel={t("En livraison", "Product")}
          hideCta={hideCta}
        />
      );
    }
  );

export default HighlightProductAdrienCachot;
